import React, {useReducer, useState} from 'react';
import {MatchRow} from 'app/components/match/matchRow/MatchRow';
import {ContentAdDesktop} from 'app/styles/content';
import {Helmet} from 'react-helmet-async';
import {string} from 'prop-types';
import {fetchTeamMatches, overwriteMatches} from 'app/routes/team/TeamPageActions';
import {LoadingIndicator, LOADING_MESSAGE} from '@fupa/fupa-uikit';
import {StreamPlaceholder} from 'app/styles/stream';
import {useLocation, useParams} from 'react-router';
import {CompetitionCaption, Subheading} from 'app/styles/heading';
import {MatchesICLFab} from 'app/components/floatingAction/MatchesICLFab';
import {useEffectAfterMount, useFetchData, useRestoreData, useDataFetchFinished} from 'app/hooks/dataHooks';
import {TeamMatchesPageReducer} from 'app/routes/team/TeamPageReducer';
import {useInfiniteScrollComponentState} from 'app/hooks/useInfiniteScroll';
import {getPointer} from 'app/helpers/getPointer';
import {formatDateWithWeekday} from 'app/helpers/dateHelpers';
import {AdSlot} from 'app/components/ads/AdSlot';
import styled, {css} from 'styled-components';
import {ButtonGroup} from 'app/components/button/ButtonGroup';
import queryString from 'query-string';
import {MatchesPagesWrapper} from 'app/components/match/matchRow/MatchRowStyles';

const ScSubheading = styled(Subheading)`
  &&& {
    ${props =>
      props.isFirstElementInList &&
      css`
        padding-top: 0;
      `}
  }
`;

const TeamMatchesPage = ({defaultMetaDesc, dispatchRedux}) => {
  const {restoredData} = useRestoreData('TeamMatchesPage');
  const {teamSlug} = useParams();

  const location = useLocation();
  const search = location.search;
  const searchQuery = queryString.parse(search);

  const initData = {
    isFetching: false,
    items: [],
    nextUrl: '',
    error: null,
  };
  const initialState = restoredData ?? initData;

  const [state, dispatch] = useReducer(TeamMatchesPageReducer, initialState);
  const {items, nextUrl, isFetching, error} = state;
  const loadingDataFinished = useDataFetchFinished(isFetching);
  const [activeButton, setActiveButton] = useState(getPointer(searchQuery));

  const loadData = (reloading, reset) => {
    const componentState = reset ? initData : state;
    const pointerQuery = getPointer(searchQuery);
    fetchTeamMatches(teamSlug, pointerQuery, reloading, dispatchRedux)(dispatch, componentState);
  };
  const data = {items, nextUrl};
  useInfiniteScrollComponentState(loadData, items.length);
  useFetchData(state, loadData, 'TeamMatchesPage');

  const title = 'Spiele - ';
  const meta = [{name: 'description', content: `${title}${defaultMetaDesc}`}];

  const canonical = searchQuery?.pointer ? (
    <link rel='canonical' href={`https://www.fupa.net/team/${teamSlug}/matches`} />
  ) : null;

  useEffectAfterMount(() => {
    // Request data on switching matches by navigation with toggle buttons
    const pointer = getPointer(searchQuery);
    setActiveButton(pointer);
    dispatch(overwriteMatches(initData));
    loadData(null, true);
  }, [location]);

  const showLoadingMatchesNext = isFetching ? (
    <LoadingIndicator message={LOADING_MESSAGE.loadingMatches} />
  ) : error ? (
    <StreamPlaceholder>Beim Laden ist ein Fehler aufgetreten.</StreamPlaceholder>
  ) : null;

  let lastComp = '';
  const rows = matches => {
    return matches.map((singleMatch, index, array) => {
      const lastElem = array[index - 1] || {competition: {slug: lastComp}};

      const matchRowCaptionValue = singleMatch.kickoff
        ? singleMatch.section === 'PRE'
          ? formatDateWithWeekday(singleMatch.kickoff)
          : formatDateWithWeekday(singleMatch.kickoff, true)
        : null;

      const matchRow = (
        <MatchRow
          match={singleMatch}
          colored={true}
          ownTeamSlug={teamSlug}
          caption={<CompetitionCaption variant='caption1'>{matchRowCaptionValue}</CompetitionCaption>}
        />
      );
      if (singleMatch.competition?.slug === lastElem.competition?.slug) {
        return <React.Fragment key={singleMatch.slug}>{matchRow}</React.Fragment>;
      } else {
        lastComp = singleMatch.competition?.slug;
        return (
          <React.Fragment key={singleMatch.slug}>
            <ScSubheading isFirstElementInList={index === 0} variant='subheading2' center={true}>
              {singleMatch.competition?.name ?? 'Unbekannt'}
            </ScSubheading>
            {matchRow}
          </React.Fragment>
        );
      }
    });
  };

  const activeButtonText = activeButton === 'prev' ? 'vergangenen' : 'aktuellen';
  const matchDays = data.items.length ? (
    rows(data.items)
  ) : !isFetching ? (
    <StreamPlaceholder>Keine {activeButtonText} Spiele verfügbar.</StreamPlaceholder>
  ) : (
    <div />
  );

  if (loadingDataFinished) {
    matchDays.length > 1 ? matchDays.splice(1, 0, <AdSlot key='Content_1' adUnit='Content_1' />) : null;
    matchDays.length > 4 ? matchDays.splice(6, 0, <AdSlot key='Rectangle_1' adUnit='Rectangle_1' />) : null;
  }

  let matchesICL;
  matchesICL = <MatchesICLFab teamSlug={teamSlug} />;

  const buttons = [
    {
      url: `/team/${teamSlug}/matches`,
      label: 'Aktuelle Spiele',
      value: 'current',
    },
    {
      url: `/team/${teamSlug}/matches?pointer=prev`,
      label: 'Vergangene Spiele',
      value: 'prev',
    },
  ];

  const subNav = (
    <ButtonGroup activeValue={activeButton} onChange={value => setActiveButton(value)} buttons={buttons} />
  );

  return (
    <ContentAdDesktop top='7.125rem' fabBtn={matchesICL} superbannerOnly>
      {subNav}
      <MatchesPagesWrapper>{matchDays}</MatchesPagesWrapper>
      {showLoadingMatchesNext}
      <Helmet title={title} meta={meta}>
        {canonical}
      </Helmet>
    </ContentAdDesktop>
  );
};

TeamMatchesPage.propTypes = {
  defaultMetaDesc: string.isRequired,
};

export default TeamMatchesPage;
